import React from 'react'
import type { ListingResponse } from '@certi/shared/types'
import { Pill } from '@/components/ui/Pill'

interface ListingCardProps {
  listing: ListingResponse
  isFavorite?: boolean
  onPress?: () => void
  onToggleFavorite?: (id: string) => void
}



export default function ListingCard({ 
  listing, 
  isFavorite = false, 
  onPress, 
  onToggleFavorite 
}: ListingCardProps) {
  const formatTagLabel = (tag: string) => tag.replace(/_/g, ' ')

  const handleClick = () => {
    if (onPress) {
      onPress()
    } else {
      console.log('View listing:', listing.id)
    }
  }

  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent card click
    if (onToggleFavorite) {
      onToggleFavorite(listing.id)
    }
  }

  // Extract tag types for overlays
  const isWorn = ['GAME_WORN', 'WARMUP_WORN'].includes(listing.condition)
  const isSigned = listing.tags?.includes('AUTOGRAPHED')
  const highlightTag = listing.tags?.find(t =>
    ['LIMITED_EDITION', 'PLAYOFF_GAME', 'FINAL_MATCH', 'DEBUT_GAME', 'MVP_GAME'].includes(t)
  ) ?? null

  const price = Number(listing.price || 0);
  const priceLabel = price ? `US ${price.toLocaleString("en-US", { style: "currency", currency: "USD" }).replace("$","$")}` : "—";
  const categoryLabel = listing.category.replace(/_/g, ' ').toLowerCase()
  const conditionLabel = listing.condition.replace(/_/g, ' ').toLowerCase()

  return (
    <div className="cursor-pointer select-none" onClick={handleClick}>
      {/* IMAGE TILE WITH OVERLAYS */}
      <div className="relative aspect-[4/5] bg-gray-100 overflow-hidden">
        {listing.mediaUrls && listing.mediaUrls.length > 0 ? (
          <img
            src={listing.mediaUrls[0]}
            alt={listing.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
              target.parentElement!.innerHTML = `
                <div class="w-full h-full flex items-center justify-center bg-gray-100">
                  <svg class="w-12 h-12 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                  </svg>
                </div>
              `
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <svg className="w-12 h-12 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
            </svg>
          </div>
        )}

        {/* top-left: WORN / SIGNED */}
        <div className="absolute top-2 left-2 flex gap-1">
          {isWorn && <Pill variant="solidDark">WORN</Pill>}
          {isSigned && <Pill variant="solidDark">SIGNED</Pill>}
        </div>

        {/* top-right: heart */}
        <button
          onClick={(e) => { e.stopPropagation(); handleToggleFavorite(e); }}
          className="absolute top-2 right-2 grid place-items-center w-7 h-7 rounded-full bg-white/90 hover:bg-white shadow"
          aria-label="favorite"
        >
          {isFavorite ? "❤" : "♡"}
        </button>

        {/* bottom-left: valid listing highlight tags */}
        {highlightTag && (
          <div className="absolute bottom-2 left-2">
            <Pill variant="outlineLight">{formatTagLabel(highlightTag)}</Pill>
          </div>
        )}
      </div>

      {/* CAPTION */}
      <div className="pt-1">
        <h3 className="text-sm font-semibold text-gray-900 line-clamp-1">
          {listing.playerName ?? listing.title}
        </h3>
        <div className="text-xs text-gray-600">price</div>
        <div className="text-base font-bold text-gray-900">{priceLabel}</div>
        <div className="text-xs text-gray-600 capitalize">{categoryLabel} · {conditionLabel}</div>
      </div>
    </div>
  )
}
